// Lightweight ticker picker for the chart's "compare against" overlay.
// Sources: held positions + watchlist + known instruments.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { GitCompareArrows, X } from 'lucide-react';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';

type Props = {
  excludeTicker: string;
  value?: string;
  onChange: (ticker: string | undefined) => void;
};

export function CompareTickerPicker({ excludeTicker, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const positions = useQuery({ queryKey: ['positions'], queryFn: api.positions });
  const watchlist = useQuery({ queryKey: ['watchlist'], queryFn: api.watchlist });

  const options = useMemo(() => {
    const seen = new Set<string>();
    const out: { ticker: string; name: string; group: string }[] = [];
    for (const p of positions.data ?? []) {
      if (p.instrument.ticker === excludeTicker) continue;
      if (seen.has(p.instrument.ticker)) continue;
      seen.add(p.instrument.ticker);
      out.push({ ticker: p.instrument.ticker, name: p.instrument.name, group: 'Held' });
    }
    for (const w of watchlist.data?.items ?? []) {
      if (w.ticker === excludeTicker) continue;
      if (seen.has(w.ticker)) continue;
      seen.add(w.ticker);
      out.push({ ticker: w.ticker, name: w.name, group: 'Watching' });
    }
    return out;
  }, [positions.data, watchlist.data, excludeTicker]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-md border transition-colors',
          value
            ? 'border-amber-500/40 bg-amber-500/10 text-amber-200'
            : 'border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700',
        )}
      >
        <GitCompareArrows className="size-3.5" />
        {value ? `vs ${value.split('_')[0]}` : 'Compare'}
        {value && (
          <X
            className="size-3 hover:text-rose-400"
            onClick={(e) => {
              e.stopPropagation();
              onChange(undefined);
              setOpen(false);
            }}
          />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-56 rounded-md border border-zinc-800 bg-zinc-950 shadow-2xl p-1 max-h-72 overflow-y-auto">
          {options.length === 0 && (
            <div className="text-xs text-zinc-400 px-3 py-2">No other tickers available.</div>
          )}
          {options.map((o) => (
            <button
              type="button"
              key={o.ticker}
              onClick={() => {
                onChange(o.ticker);
                setOpen(false);
              }}
              className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-xs hover:bg-zinc-900"
            >
              <span className="font-mono text-zinc-200 w-12 shrink-0">
                {o.ticker.split('_')[0]}
              </span>
              <span className="text-zinc-400 truncate flex-1">{o.name}</span>
              <span className="text-[10px] text-zinc-400">{o.group}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
